// @file Map location selector store

import { trackEvent } from '@@/bits/analytics'
import { browserCan } from '@@/bits/browser'
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import type {
  AutocompletePrediction,
  GeocoderResult,
  GeoMap,
  PlaceResult,
  PlacesService as PlacesServiceType,
} from '@@/bits/map'
import {
  AutocompleteService,
  AutocompleteSessionToken,
  Geocoder,
  GeocoderStatus,
  getLocationNameSuggestionsForMap,
  initMap,
  LatLng,
  PlacesService,
  PlacesServiceStatus,
  RankBy,
} from '@@/bits/map'
import DropOverlay from '@@/bits/map_drop_overlay'
import { getMarkerColorsFromThemeId } from '@@/bits/map_theme'
import { useGlobalAlertDialogStore } from '@@/pinia/global_alert_dialog'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { usePostComposerModalStore } from '@@/pinia/post_composer_modal_store'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceComposerModalTagInputsStore } from '@@/pinia/surface_composer_modal_tag_inputs'
import { useSurfaceContainerSizeStore } from '@@/pinia/surface_container_size'
import { useSurfaceDraftsStore } from '@@/pinia/surface_drafts'
import { useSurfaceMapStore } from '@@/pinia/surface_map'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import type { PinDropSuggestion, Point } from '@@/types'
import { processedUrl } from '@padlet/vivaldi-client'
import { captureException } from '@sentry/vue'
import { debounce } from 'es-toolkit'
import { defineStore } from 'pinia'
import type { ComputedRef } from 'vue'
import { computed, ref, watch } from 'vue'

export const useMapLocationSelectorStore = defineStore('mapLocationSelectorStore', () => {
  const surfaceMapStore = useSurfaceMapStore()
  const surfaceDraftsStore = useSurfaceDraftsStore()
  const globalSnackbarStore = useGlobalSnackbarStore()
  const globalAlertDialogStore = useGlobalAlertDialogStore()
  const surfacePostsStore = useSurfacePostsStore()
  const surfaceComposerModalTagInputsStore = useSurfaceComposerModalTagInputsStore()
  const surfaceContainerSizeStore = useSurfaceContainerSizeStore()
  const surfaceStore = useSurfaceStore()

  const currentThemeId = surfaceMapStore.currentThemeId

  const placesService = ref<PlacesServiceType | undefined>(undefined)
  const autocompleteService = new AutocompleteService()
  const sessionToken = new AutocompleteSessionToken()
  const geocoder = ref<Geocoder | undefined>(undefined)
  const dropOverlay = ref<DropOverlay | null>(null)

  interface LocationData {
    placeId: string
    name: string
    latitude?: number
    longitude?: number
  }

  const suggestions = ref<LocationData[]>([])

  function fromPixelToLatLng(x: number, y: number): LatLng | null {
    return dropOverlay.value?.fromPixelToLatLng(x, y) ?? null
  }

  const canGeolocate = browserCan('geolocation')

  const isSearchingNearbyLocations = ref<boolean>(false)

  function setSuggestionsWithCurrentLocation(): void {
    // Try to search nearby places when the location marker button is clicked
    // Note: The nearby places API is more expensive than autocomplete/text search API since it gets a lot of data that we don't actually need :(
    // Reference: https://developers.google.com/maps/documentation/places/web-service/search#PlaceSearchRequests
    window.navigator.geolocation.getCurrentPosition(
      (position) => {
        placesService.value?.nearbySearch(
          {
            location: new LatLng(position.coords.latitude, position.coords.longitude),
            // radius in meters
            radius: 200,
            rankBy: RankBy.PROMINENCE,
          },
          (places: PlaceResult[] | null, status: PlacesServiceStatus) => {
            if (status === PlacesServiceStatus.OK && places != null) {
              suggestions.value = places
                .filter((place) => place.place_id !== undefined)
                .map((place) => ({
                  placeId: place.place_id as string,
                  // NOTE: from Places API's document: https://developers.google.com/maps/documentation/javascript/reference/places-service#PlaceResult.name
                  // In the case of user entered Places, this is the raw text, as typed by the user. Please exercise caution when using this data, as malicious users may try to use it as a vector for code injection attacks (See http://en.wikipedia.org/wiki/Code_injection)
                  // It should be fine for our use case
                  name: place.name ?? '',
                  longitude: place.geometry?.location?.lng(),
                  latitude: place.geometry?.location?.lat(),
                }))
            }
          },
        )
        isSearchingNearbyLocations.value = false
      },
      (error) => {
        // Copied from map_browser.vue, just show an alert when we don't have permission for now
        if (error.code === error.PERMISSION_DENIED) {
          globalAlertDialogStore.openAlertDialog({
            title: __("We don't have permission to see your location"),
            body: __(
              "You may want to change that in your browser settings. Don't worry, we don't store your location on our servers.",
            ),
            closeButtonText: __('Okay'),
          })
        } else {
          captureException(error)
        }
        isSearchingNearbyLocations.value = false
      },
    )
  }

  const MAX_SUGGESTIONS = 4

  function setSuggestionsWithSearchQuery(searchQuery: string): void {
    void autocompleteService.getPlacePredictions(
      {
        bounds: surfaceMapStore.map?.getBounds() ?? undefined,
        input: searchQuery,
        sessionToken,
      },
      (predictions: AutocompletePrediction[] | null, status: PlacesServiceStatus) => {
        if (status === PlacesServiceStatus.OK && predictions != null) {
          suggestions.value = predictions.slice(0, MAX_SUGGESTIONS).map((prediction) => ({
            placeId: prediction.place_id,
            name: prediction.description ?? '',
          }))
        }
      },
    )
  }

  function pickLocationWithSearchSuggestion(suggestion: LocationData): void {
    trackEvent('Map Location Selector', 'Pick location with search suggestion')
    placesService.value?.getDetails(
      {
        placeId: suggestion.placeId,
        // only get geometry.location field to reduce cost (Places API can also get contact and atmosphere data
        // (e.g. phone number and number of stars for restaurants)
        fields: ['geometry.location'],
        sessionToken,
      },
      (result, status) => {
        if (status === PlacesServiceStatus.OK && result?.geometry != null) {
          surfaceMapStore.pickLocation({ latLng: result.geometry.location, name: suggestion.name })
        }
      },
    )
  }

  function clearSuggestions(): void {
    suggestions.value = []
  }

  const dragImage: HTMLImageElement = new Image()
  function setDragImageWithThemeId(themeId: number): void {
    const markerColors = getMarkerColorsFromThemeId(themeId)
    const { fill, stroke } = markerColors
    const image = `https://padlet.com/static/v1/map-marker.svg?fill=${fill.default}&stroke=${stroke}`
    dragImage.src = processedUrl(image, { width: 36, height: 48, dpr: 1 })
  }

  const locationFound = ref<boolean>(false)

  function handleDragStart(e: DragEvent): void {
    e.dataTransfer?.setDragImage(dragImage, 18, 36) // Sets image that will move with cursor
    document.addEventListener('drop', handleDrop, { once: true })
    const source = e.target as HTMLElement
    source.addEventListener('dragend', handleDragEnd, { once: true }) // note: escape key triggers dragend event
    locationFound.value = false
    globalSnackbarStore.setSnackbar({
      message: __('Press ESC to cancel'),
      persist: true,
    })

    setTimeout(() => {
      if (surfaceDraftsStore.activeDraftCid === null) {
        globalSnackbarStore.genericFetchError()
        throw new Error('No active draft')
      }
      if (surfaceComposerModalTagInputsStore.isShowingTagMenu) {
        surfaceComposerModalTagInputsStore.closePopover() // We need to directly call closePopover() here instead of emitting the 'cancel' event in SurfaceComposerModalTagLocationMenu.vue to avoid messing with this function
      }
      if (surfacePostsStore.isExistingPost(surfaceDraftsStore.activeDraftCid)) {
        surfaceMapStore.startPickingNewLocation({ postCid: surfaceDraftsStore.activeDraftCid })
        surfacePostsStore.stopEditingPost()
      } else {
        usePostComposerModalStore().minimizeComposerModal(surfaceDraftsStore.activeDraftCid)
      }
    }, 0)
  }

  function handleDragEnd(): void {
    // If the dragend event is triggered by the escape key
    if (!locationFound.value) {
      if (surfaceMapStore.postWithLocationBeingEditedCid !== null) {
        surfacePostsStore.startEditingPost({ postCid: surfaceMapStore.postWithLocationBeingEditedCid }) // Reopen the post that was being edited
        surfaceMapStore.stopPickingNewLocation() // Set postWithLocationBeingEditedCid to null
      } else {
        usePostComposerModalStore().restoreComposerModalState(surfaceDraftsStore.activeDraftCid as string)
      }
      document.removeEventListener('drop', handleDrop)
    }
    globalSnackbarStore.removeAllSnackbars()
  }

  function handleDrop(e: DragEvent): void {
    const location = fromPixelToLatLng(e.clientX, e.clientY)
    if (location != null) {
      void geocoder.value?.geocode(
        {
          location,
        },
        (result: GeocoderResult[] | null, status: google.maps.GeocoderStatus) => {
          if (status === GeocoderStatus.OK && result != null) {
            displayPinDropSelectionMenu(location, result)
          } else {
            displayPinDropSelectionMenu(location, [])
          }
        },
      )
    }
    locationFound.value = true
  }

  function displayPinDropSelectionMenu(location: LatLng, results: GeocoderResult[]): void {
    const endResults = getLocationNameSuggestionsForMap(location, results, surfaceMapStore.map as GeoMap)
    surfaceMapStore.setMapPinDropSuggestions({
      suggestions: endResults,
      location,
    })
  }

  const placedPinLocation: ComputedRef<LatLng | null> = computed(() =>
    fromPixelToLatLng(
      surfaceContainerSizeStore.surfaceContainerWidth / 2,
      (surfaceContainerSizeStore.surfaceContainerHeight - 360) / 2, // 360 is the height of the location suggestions menu
    ),
  )

  let stopUpdatingSuggestions: (() => void) | null = null

  function placePinAtCenterOfVisibleMap(): void {
    surfaceMapStore.setIsPlacingPin(true)
    surfaceMapStore.setDraftWithLocationBeingEditedCid(surfaceDraftsStore.activeDraftCid)
    if (surfaceDraftsStore.activeDraft?.location_point != null) {
      setCenterOfVisibleMaptoActiveDraftLocation(surfaceDraftsStore.activeDraft?.location_point)
    }
    hideDraftBeingEdited()
    // Update suggestions whenever the user moves the map to change the placed pin's location
    stopUpdatingSuggestions = watch(
      [
        placedPinLocation,
        () => surfaceMapStore.isPlacingPin, // When the user cancels placing a pin, stop updating suggestions
      ],
      debouncedUpdatePlacedPinSuggestions,
      {
        immediate: true,
      },
    )
  }

  function hideDraftBeingEdited(): void {
    if (surfaceMapStore.draftWithLocationBeingEditedCid == null) {
      globalSnackbarStore.genericFetchError()
      throw new Error('No draft found')
    }
    if (surfacePostsStore.isExistingPost(surfaceMapStore.draftWithLocationBeingEditedCid)) {
      // If the draft is an existing post, call stopEditingPost() to set activeDraftCid to null to close the post's composer modal
      surfacePostsStore.stopEditingPost()
      surfaceComposerModalTagInputsStore.closePopover()
    } else if (surfaceContainerSizeStore.isPhone) {
      // On phone, call stopEditingDraft() to set activeDraftCid to null. This seems to be the only way to close or minimize the composer modal on phone.
      surfaceDraftsStore.stopEditingDraft()
    } else {
      // On tablet and other touchable devices, call minimizeComposerModal() to minimize the composer modal
      usePostComposerModalStore().minimizeComposerModal(surfaceMapStore.draftWithLocationBeingEditedCid)
    }
  }

  const debouncedUpdatePlacedPinSuggestions = debounce(() => {
    if (!surfaceMapStore.isPlacingPin) {
      stopUpdatingSuggestions?.()
      stopUpdatingSuggestions = null
    }
    const newPlacedPinLocation = placedPinLocation.value
    if (newPlacedPinLocation == null) return
    geocoder.value?.geocode(
      {
        location: newPlacedPinLocation,
      },
      (results, status) => {
        if (results != null && status === GeocoderStatus.OK) {
          displayPlacedPinSuggestionsInDrawer(newPlacedPinLocation, results)
        } else {
          displayPlacedPinSuggestionsInDrawer(newPlacedPinLocation, [])
        }
      },
    )
  }, 150)

  function setCenterOfVisibleMaptoActiveDraftLocation(locationPoint: Point): void {
    if (dropOverlay.value == null) return
    const pixel: google.maps.Point = dropOverlay.value.getProjection().fromLatLngToContainerPixel(
      new LatLng({
        lat: locationPoint.latitude,
        lng: locationPoint.longitude,
      }),
    )
    pixel.y += 180 // 180 is half the height of the suggestions menu
    pixel.y -= 24 // 24px for padding. refer to padding constant in map.ts vuexstore
    const newLatLng: LatLng = dropOverlay.value.getProjection().fromContainerPixelToLatLng(pixel)
    surfaceMapStore.map?.panTo(newLatLng)
  }

  function cancelPlacingPin(): void {
    surfaceMapStore.setIsPlacingPin(false)
    showDraftBeingEdited()
    surfaceMapStore.setDraftWithLocationBeingEditedCid(null)
    surfaceMapStore.clearPlacedPinSuggestions()
  }

  function showDraftBeingEdited(): void {
    if (surfaceMapStore.draftWithLocationBeingEditedCid == null) {
      globalSnackbarStore.genericFetchError()
      throw new Error('No draft found')
    }
    if (surfacePostsStore.isExistingPost(surfaceMapStore.draftWithLocationBeingEditedCid)) {
      surfacePostsStore.startEditingPost({ postCid: surfaceMapStore.draftWithLocationBeingEditedCid })
    } else if (surfaceContainerSizeStore.isPhone) {
      surfaceDraftsStore.startEditingDraft(surfaceMapStore.draftWithLocationBeingEditedCid)
    } else {
      usePostComposerModalStore().expandComposerModal(surfaceMapStore.draftWithLocationBeingEditedCid)
    }
  }

  function displayPlacedPinSuggestionsInDrawer(location: LatLng, results: GeocoderResult[]): void {
    const endResults: PinDropSuggestion[] = getLocationNameSuggestionsForMap(
      location,
      results,
      surfaceMapStore.map as GeoMap,
    )
    surfaceMapStore.setPlacedPinSuggestions(endResults, location)
  }

  function pickLocationWithPinDropSuggestion(pinDropSuggestion: PinDropSuggestion): void {
    trackEvent('Map Location Selector', 'Pick location with dropped pin suggestion')
    if (isAppUsing('locationSelectorV2') && surfaceMapStore.isPlacingPin) {
      surfaceMapStore.pickLocation({
        latLng: placedPinLocation.value,
        name: pinDropSuggestion.locationName,
      })
      surfaceMapStore.clearPlacedPinSuggestions()
      if (useSurfaceComposerModalTagInputsStore().xLocationMenu) {
        useSurfaceComposerModalTagInputsStore().closePopover()
      }
    } else {
      surfaceMapStore.pickLocation({
        latLng: surfaceMapStore.pinDropLocation,
        name: pinDropSuggestion.locationName,
      })
      surfaceMapStore.clearMapPinDropSuggestions()
    }
  }

  function initialize(): void {
    if (!surfaceStore.isMap) {
      // For non-map padlets, we don't render the map
      // so we create a virtual element to initialize it
      const virtualEl = document.createElement('div')
      const map = initMap(virtualEl, 0, 0, 1)
      placesService.value = new PlacesService(map)
      geocoder.value = new Geocoder()
      return
    } else if (surfaceMapStore.map == null) {
      throw new Error('Map not initialized')
    }
    setDragImageWithThemeId(currentThemeId)
    placesService.value = new PlacesService(surfaceMapStore.map)
    dropOverlay.value = new DropOverlay(surfaceMapStore.map)
    geocoder.value = new Geocoder()
  }

  return {
    autocompleteService,
    sessionToken,
    geocoder,
    canGeolocate,
    suggestions,
    placedPinLocation,
    isSearchingNearbyLocations,
    initialize,
    pickLocationWithSearchSuggestion,
    handleDragStart,
    handleDrop,
    setSuggestionsWithCurrentLocation,
    setSuggestionsWithSearchQuery,
    clearSuggestions,
    placePinAtCenterOfVisibleMap,
    pickLocationWithPinDropSuggestion,
    cancelPlacingPin,
  }
})
